
import React from "react";

interface EventLocationProps {
  locationType: "physical" | "online";
  locationAddress: string | null;
  locationCity: string | null;
  locationCountry: string | null;
  locationUrl: string | null;
  locationRevealDate?: string | null;
}

export default function EventLocation({
  locationType,
  locationAddress,
  locationCity,
  locationCountry,
  locationUrl,
  locationRevealDate,
}: EventLocationProps) {

  /* ------------------------------------------------------------------ */
  /* Check if location is still hidden (reveal date hasn't passed yet)   */
  /* ------------------------------------------------------------------ */
  const isLocationHidden =
    !!locationRevealDate && new Date() < new Date(locationRevealDate);

  const formatRevealDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /* ------------------------------------------------------------------ */
  /* 🔒 Location is hidden — show locked state                          */
  /* ------------------------------------------------------------------ */
  if (isLocationHidden) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8">
        <h2 className="text-lg font-bold text-gray-900 mb-5">Location</h2>
        <div className="flex flex-col items-center justify-center py-6 px-4 bg-amber-50 border border-amber-100 rounded-xl text-center gap-3">
          <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center">
            <i className="ri-lock-line text-amber-500 text-2xl" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900 mb-1">
              Location Not Yet Revealed
            </p>
            <p className="text-xs text-gray-500 leading-relaxed max-w-xs mx-auto">
              The organizer will reveal the exact location on
            </p>
            <p className="text-sm font-semibold text-amber-600 mt-1">
              {formatRevealDateTime(locationRevealDate!)}
            </p>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-100 px-3 py-1.5 rounded-full">
            <i className="ri-time-line" />
            Check back closer to the event date
          </div>
        </div>
      </div>
    );
  }

  /** Determine which kind of location should be displayed */
  const hasPhysicalLocation =
    locationType === "physical" && Boolean(locationAddress);
  const hasOnlineLink = locationType === "online" && Boolean(locationUrl);

  /* ------------------------------------------------------------------ */
  /* 1️⃣ Online event *without* a link – show placeholder information   */
  /* ------------------------------------------------------------------ */
  if (!hasPhysicalLocation && !hasOnlineLink && locationType === "online") {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8">
        <h2 className="text-lg font-bold text-gray-900 mb-5">Location</h2>
        <div className="flex items-center gap-4 p-4 bg-teal-50/50 rounded-lg">
          <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-global-line text-teal-600 text-xl" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">Online Event</p>
            <p className="text-xs text-gray-500 mt-0.5">
              The event link will be shared after registration
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* 2️⃣ No location data at all – render nothing (fallback)            */
  /* ------------------------------------------------------------------ */
  if (!hasPhysicalLocation && !hasOnlineLink) {
    return null;
  }

  /* ------------------------------------------------------------------ */
  /* 3️⃣ Render the actual location block (physical, online or both)   */
  /* ------------------------------------------------------------------ */
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8">
      <h2 className="text-lg font-bold text-gray-900 mb-5">Location</h2>

      {/* ---------- Physical location ---------- */}
      {hasPhysicalLocation && (
        <div>
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-map-pin-line text-teal-600 text-xl" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Venue Address
              </p>
              <p className="text-sm text-gray-600 mt-0.5">
                {locationAddress}
                {locationCity && `, ${locationCity}`}
                {locationCountry && `, ${locationCountry}`}
              </p>
            </div>
          </div>

          <div className="w-full h-48 rounded-lg overflow-hidden border border-gray-100">
            <iframe
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(
                `${locationAddress ?? ""}${
                  locationCity ? `, ${locationCity}` : ""
                }${locationCountry ? `, ${locationCountry}` : ""}`
              )}`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              title="Event location map"
            />
          </div>
        </div>
      )}

      {/* ---------- Online location ---------- */}
      {hasOnlineLink && (
        <div className="flex items-center gap-4 p-4 bg-teal-50/50 rounded-lg">
          <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-links-line text-teal-600 text-xl" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">Online Event</p>
            <a
              href={locationUrl!}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="text-sm text-teal-600 hover:text-teal-700 break-all transition-colors"
            >
              {locationUrl}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
